<template>
  <Dialog v-model:open="isOpen">
    <DialogTrigger as-child>
      <slot name="trigger" />
    </DialogTrigger>

    <DialogContent
      :class="wrpClass"
      class="sm:max-w-[425px]"
      @close="handleClose"
      @escape-key-down="handleClose"
      @interact-outside="handleClose"
    >
      <DialogHeader v-if="!noHeader">
        <slot name="header">
          <DialogTitle>{{ title }}</DialogTitle>
          <DialogDescription>
            {{ description }}
          </DialogDescription>
        </slot>
      </DialogHeader>

      <slot />

      <DialogFooter v-if="!noFooter">
        <slot name="footer">
          <BaseButton
            text="button.cancel"
            variant="neutral"
            @click="tryClose"
          />
          <BaseButton text="button.continue" @click="emit('submit')" />
        </slot>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script lang="ts" setup>
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import type { ModalProps } from "@/types/components";
import { BaseButton } from "@/components/Base";
import { computed, defineProps, defineEmits, defineModel } from "vue";

type Emits = {
  (e: "submit"): void;
};

const props = defineProps<ModalProps>();
const emit = defineEmits<Emits>();
const isOpen = defineModel<boolean>({ required: true });

// Prevent closing if `open` prop is provided
const isControlled = computed(() => props.open !== undefined);

const tryClose = () => {
  if (!isControlled.value) {
    isOpen.value = false;
  }
};

const handleClose = (event: Event) => {
  if (isControlled.value) {
    event.preventDefault();
  } else {
    isOpen.value = false;
  }
};
</script>
